import { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  MessageSquare, Send, Hash, Plus, Users, Search, Pin, Check,
  ChevronRight, Circle, Paperclip, Star, UserCheck, Zap, Clock,
  AlertTriangle, Shield, Package, DollarSign, Wrench, Headphones,
  UserX, RefreshCw, ArrowUp, Filter, X, ChevronDown, Bell, MapPin
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

// ─── Severity Config ───
const SEVERITY = {
  low:      { color: "#64748B", bg: "#F1F5F9", label: "Low" },
  medium:   { color: "#D97706", bg: "#FFFBEB", label: "Medium" },
  high:     { color: "#EA580C", bg: "#FFF7ED", label: "High" },
  urgent:   { color: "#DC2626", bg: "#FEF2F2", label: "Urgent" },
  critical: { color: "#BE123C", bg: "#FFF1F2", label: "Critical" },
};

const STATUS_FLOW = {
  new: { label: "New", color: "#3B82F6", next: ["acknowledged"] },
  acknowledged: { label: "Acknowledged", color: "#8B5CF6", next: ["in_progress", "assigned"] },
  in_progress: { label: "In Progress", color: "#F59E0B", next: ["resolved", "waiting"] },
  assigned: { label: "Assigned", color: "#7C3AED", next: ["in_progress"] },
  waiting: { label: "Waiting", color: "#94A3B8", next: ["in_progress", "resolved"] },
  resolved: { label: "Resolved", color: "#16A34A", next: ["closed"] },
  closed: { label: "Closed", color: "#64748B", next: [] },
};

const CATEGORY_ICONS = {
  client_complaint: Headphones, inventory_shortage: Package, equipment_issue: Wrench,
  payroll_question: DollarSign, vip_concern: Star, redo_risk: AlertTriangle,
  shift_coverage: Users, refund_request: DollarSign, manager_needed: Shield,
  safety_incident: AlertTriangle, front_desk_help: UserCheck, general: MessageSquare,
};

// ─── Severity Badge ───
function SeverityBadge({ severity }) {
  const s = SEVERITY[severity] || SEVERITY.low;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: s.bg, color: s.color }}>
      {severity === "critical" || severity === "urgent" ? <AlertTriangle className="w-2.5 h-2.5" strokeWidth={2.5} /> : null}
      {s.label}
    </span>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_FLOW[status] || STATUS_FLOW.new;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold" style={{ backgroundColor: `${s.color}15`, color: s.color }}>
      <Circle className="w-2 h-2 fill-current" /> {s.label}
    </span>
  );
}

// ─── Escalation Card ───
function EscalationCard({ esc, onClick, locations }) {
  const Icon = CATEGORY_ICONS[esc.category] || MessageSquare;
  const sev = SEVERITY[esc.severity] || SEVERITY.low;
  const age = Math.round((Date.now() - new Date(esc.created_at).getTime()) / 60000);
  const ageLabel = age < 60 ? `${age}m` : age < 1440 ? `${Math.round(age/60)}h` : `${Math.round(age/1440)}d`;
  const locName = (locations || []).find(l => l.location_id === esc.location_id)?.name;

  return (
    <div data-testid={`escalation-${esc.escalation_id}`} onClick={() => onClick(esc)}
      className={`bg-white border rounded-xl p-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${
        esc.severity === "critical" ? "border-rose-300 shadow-rose-100" :
        esc.severity === "urgent" ? "border-red-200" : "border-slate-100"
      }`}>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: sev.bg }}>
          <Icon className="w-4 h-4" style={{ color: sev.color }} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <SeverityBadge severity={esc.severity} />
            <StatusBadge status={esc.status} />
            <span className="text-[10px] text-slate-400 ml-auto font-['Manrope']">{ageLabel} ago</span>
          </div>
          <p className="text-sm font-semibold text-slate-900 truncate font-['Manrope']">{esc.title}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <p className="text-xs text-slate-400 truncate font-['Manrope']">{esc.category_label} · {esc.reporter_name}</p>
            {locName && (
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500 flex items-center gap-0.5 flex-shrink-0">
                <MapPin className="w-2.5 h-2.5" strokeWidth={2} />{locName}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Escalation Detail Drawer ───
function EscalationDrawer({ esc, open, onClose, onUpdate, staff, locations }) {
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);
  const { user } = useAuth();

  if (!esc) return null;
  const Icon = CATEGORY_ICONS[esc.category] || MessageSquare;
  const sev = SEVERITY[esc.severity] || SEVERITY.low;
  const statusInfo = STATUS_FLOW[esc.status] || STATUS_FLOW.new;

  const updateStatus = async (newStatus) => {
    try {
      const res = await api.put(`/escalations/${esc.escalation_id}`, { status: newStatus });
      onUpdate(res.data);
      toast.success(`Status updated to ${STATUS_FLOW[newStatus]?.label || newStatus}`);
    } catch { toast.error("Failed to update"); }
  };

  const updateSeverity = async (newSev) => {
    try {
      const res = await api.put(`/escalations/${esc.escalation_id}`, { severity: newSev });
      onUpdate(res.data);
    } catch { toast.error("Failed"); }
  };

  const assignTo = async (userId, userName) => {
    try {
      const res = await api.put(`/escalations/${esc.escalation_id}`, { owner_id: userId, owner_name: userName, status: "assigned" });
      onUpdate(res.data);
      toast.success(`Assigned to ${userName}`);
    } catch { toast.error("Failed"); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    setPosting(true);
    try {
      await api.post(`/escalations/${esc.escalation_id}/comments`, { content: comment });
      setComment("");
      const res = await api.get(`/escalations/${esc.escalation_id}`);
      onUpdate(res.data);
    } catch { toast.error("Failed"); }
    setPosting(false);
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-xl bg-white p-0 overflow-y-auto" data-testid="escalation-drawer">
        <div className="p-6 space-y-5">
          <SheetHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: sev.bg }}>
                <Icon className="w-5 h-5" style={{ color: sev.color }} strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <SheetTitle className="font-['Outfit'] text-xl font-semibold text-slate-900">{esc.title}</SheetTitle>
                <SheetDescription className="text-sm text-slate-400">{esc.category_label}</SheetDescription>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <SeverityBadge severity={esc.severity} />
              <StatusBadge status={esc.status} />
              <span className="text-xs text-slate-400 font-['Manrope']">#{esc.escalation_id}</span>
            </div>
          </SheetHeader>

          {/* Description */}
          <div className="bg-slate-50 rounded-xl p-4">
            <p className="text-sm text-slate-700 font-['Manrope'] leading-relaxed">{esc.description || "No description"}</p>
          </div>

          {/* Meta */}
          <div className="grid grid-cols-2 gap-3 text-xs font-['Manrope']">
            <div><span className="text-slate-400">Reporter</span><p className="font-semibold text-slate-800">{esc.reporter_name}</p></div>
            <div><span className="text-slate-400">Owner</span><p className="font-semibold text-slate-800">{esc.owner_name || "Unassigned"}</p></div>
            <div><span className="text-slate-400">Location</span><p className="font-semibold text-slate-800 flex items-center gap-1"><MapPin className="w-3 h-3 text-rose-400" />{(locations || []).find(l => l.location_id === esc.location_id)?.name || esc.location_id || "Not assigned"}</p></div>
            <div><span className="text-slate-400">Route</span><p className="font-semibold text-slate-800 capitalize">{esc.route?.replace("-", " ")}</p></div>
            <div><span className="text-slate-400">Created</span><p className="font-semibold text-slate-800">{new Date(esc.created_at).toLocaleString([], {dateStyle: "short", timeStyle: "short"})}</p></div>
          </div>

          {/* Actions */}
          <div className="space-y-3 border-t border-slate-100 pt-4">
            <h4 className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Actions</h4>
            <div className="flex flex-wrap gap-2">
              {(statusInfo.next || []).map(ns => (
                <Button key={ns} size="sm" onClick={() => updateStatus(ns)} data-testid={`status-btn-${ns}`}
                  className="h-8 rounded-lg text-xs font-semibold"
                  style={{ backgroundColor: `${STATUS_FLOW[ns]?.color}15`, color: STATUS_FLOW[ns]?.color, border: `1px solid ${STATUS_FLOW[ns]?.color}30` }}>
                  {STATUS_FLOW[ns]?.label}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              <Select onValueChange={v => updateSeverity(v)}>
                <SelectTrigger className="h-8 w-32 text-xs border-slate-200 rounded-lg"><SelectValue placeholder="Severity" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(SEVERITY).map(([k,v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select onValueChange={v => { const s = staff.find(st => st.user_id === v); if (s) assignTo(s.user_id, s.name); }}>
                <SelectTrigger className="h-8 flex-1 text-xs border-slate-200 rounded-lg"><SelectValue placeholder="Assign to..." /></SelectTrigger>
                <SelectContent>
                  {staff.filter(s => ["owner","manager","support_staff","front_desk"].includes(s.role)).map(s => (
                    <SelectItem key={s.user_id} value={s.user_id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* History */}
          {esc.history?.length > 0 && (
            <div className="space-y-2 border-t border-slate-100 pt-4">
              <h4 className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">History</h4>
              <div className="space-y-1.5">
                {esc.history.map((h, i) => (
                  <div key={`${esc.escalation_id}-h-${h.at || i}`} className="flex items-center gap-2 text-xs text-slate-500 font-['Manrope']">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                    <span className="font-medium text-slate-700">{h.by}</span>
                    <span>{h.action}</span>
                    <span className="text-slate-400 ml-auto">{new Date(h.at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comments */}
          <div className="space-y-3 border-t border-slate-100 pt-4">
            <h4 className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Comments ({esc.comments?.length || 0})</h4>
            {(esc.comments || []).map(c => (
              <div key={c.comment_id} className="bg-slate-50 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-slate-700">{c.author_name}</span>
                  <span className="text-[10px] text-slate-400">{new Date(c.created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                </div>
                <p className="text-sm text-slate-600 font-['Manrope']">{c.content}</p>
              </div>
            ))}
            <div className="flex gap-2">
              <Input value={comment} onChange={e => setComment(e.target.value)} placeholder="Add a comment..." data-testid="escalation-comment-input"
                className="h-9 border-slate-200 rounded-lg text-sm" onKeyDown={e => { if (e.key === "Enter") addComment(); }} />
              <Button size="sm" onClick={addComment} disabled={posting} className="h-9 bg-slate-900 text-white rounded-lg px-4" data-testid="add-comment-btn">
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Quick Escalation Dialog ───
function QuickEscalateDialog({ open, onClose, onCreated, sourceMessage, locations }) {
  const [category, setCategory] = useState("general");
  const [severity, setSeverity] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [locationId, setLocationId] = useState("");
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/escalations/categories").then(r => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (open) {
      if (sourceMessage) {
        setDescription(sourceMessage.content || "");
        setTitle("");
      } else {
        setTitle(""); setDescription("");
      }
      setCategory("general"); setSeverity(""); setLocationId(locations?.[0]?.location_id || "");
    }
  }, [open, sourceMessage, locations]);

  const submit = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    if (!locationId) { toast.error("Please select a location"); return; }
    setSaving(true);
    try {
      const payload = { title, description, category, severity: severity || undefined, location_id: locationId, source_message_id: sourceMessage?.message_id || "", source_message: sourceMessage?.content || "" };
      await api.post("/escalations", payload);
      toast.success("Escalation created and routed");
      onCreated();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="escalate-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-xl font-semibold">Create Escalation</DialogTitle>
          <DialogDescription>Route this issue to the right team for resolution</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1.5 block uppercase tracking-wider">Category</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="h-10 border-slate-200 rounded-xl" data-testid="esc-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1.5 block uppercase tracking-wider">Location *</label>
            <Select value={locationId} onValueChange={setLocationId}>
              <SelectTrigger className="h-10 border-slate-200 rounded-xl" data-testid="esc-location-select">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                {(locations || []).map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1.5 block uppercase tracking-wider">Title</label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Brief description of the issue" className="h-10 border-slate-200 rounded-xl" data-testid="esc-title-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1.5 block uppercase tracking-wider">Details</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Full details..."
              className="w-full h-20 px-3 py-2 border border-slate-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500" data-testid="esc-desc-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1.5 block uppercase tracking-wider">Severity</label>
            <div className="flex gap-2">
              {Object.entries(SEVERITY).map(([k, v]) => (
                <button key={k} onClick={() => setSeverity(k)} data-testid={`esc-sev-${k}`}
                  className={`flex items-center gap-1 px-3 py-2 rounded-xl border text-xs font-semibold transition-all ${
                    severity === k ? `ring-2 ring-offset-1` : 'bg-white border-slate-200 text-slate-500'
                  }`} style={severity === k ? { backgroundColor: v.bg, color: v.color, borderColor: v.color, ringColor: v.color } : {}}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          {sourceMessage && (
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
              <p className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mb-1">Source Message</p>
              <p className="text-xs text-slate-600 italic">"{sourceMessage.content?.slice(0, 120)}"</p>
            </div>
          )}
          <Button onClick={submit} disabled={saving} className="w-full h-11 gradient-primary text-white rounded-xl font-semibold shadow-md" data-testid="submit-escalation-btn">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <ArrowUp className="w-4 h-4 mr-2" />}
            {saving ? "Creating..." : "Create & Route Escalation"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Quick Action Buttons ───
const QUICK_ACTIONS = [
  { id: "client_complaint", label: "Client Complaint", icon: Headphones },
  { id: "inventory_shortage", label: "Low Stock", icon: Package },
  { id: "equipment_issue", label: "Equipment Issue", icon: Wrench },
  { id: "manager_needed", label: "Need Manager", icon: Shield },
  { id: "redo_risk", label: "Redo Risk", icon: AlertTriangle },
  { id: "shift_coverage", label: "Shift Coverage", icon: Users },
];

// ─── Main TeamHubPage ───
export default function TeamHubPage() {
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState("");
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [presence, setPresence] = useState([]);
  const [showQuickActions, setShowQuickActions] = useState(false);
  const [locations, setLocations] = useState([]);
  const { user } = useAuth();
  const msgsEndRef = useRef(null);
  const pollRef = useRef(null);

  // Escalation state
  const [tab, setTab] = useState("chat"); // "chat" | "escalations"
  const [escalations, setEscalations] = useState([]);
  const [escStats, setEscStats] = useState(null);
  const [escFilter, setEscFilter] = useState("all");
  const [selectedEsc, setSelectedEsc] = useState(null);
  const [escDrawerOpen, setEscDrawerOpen] = useState(false);
  const [showEscDialog, setShowEscDialog] = useState(false);
  const [escSourceMsg, setEscSourceMsg] = useState(null);
  const [quickEscCategory, setQuickEscCategory] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [ch, st, pr, loc] = await Promise.all([api.get("/channels"), api.get("/staff/all"), api.get("/team-hub/presence"), api.get("/locations")]);
        setChannels(ch.data); setStaff(st.data); setPresence(pr.data); setLocations(loc.data || []);
        if (ch.data.length > 0) { setActiveChannel(ch.data[0]); loadMessages(ch.data[0].channel_id); }
        loadEscalations();
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (activeChannel) {
      pollRef.current = setInterval(() => { loadMessages(activeChannel.channel_id); }, 8000);
    }
  }, [activeChannel]);

  const loadMessages = async (channelId) => {
    try {
      const res = await api.get(`/channels/${channelId}/messages`);
      setMessages(res.data);
    } catch {}
  };

  const loadEscalations = async () => {
    try {
      try { await api.post("/escalations/seed"); } catch {}
      const [escRes, statsRes] = await Promise.all([api.get("/escalations"), api.get("/escalations/stats/summary")]);
      setEscalations(escRes.data);
      setEscStats(statsRes.data);
    } catch {}
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !activeChannel) return;
    setSending(true);
    try {
      await api.post(`/channels/${activeChannel.channel_id}/messages`, { content: newMsg });
      setNewMsg("");
      await loadMessages(activeChannel.channel_id);
      setTimeout(() => msgsEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch { toast.error("Failed to send"); }
    setSending(false);
  };

  const createChannel = async () => {
    if (!newChannelName.trim()) return;
    try {
      const res = await api.post("/channels", { name: newChannelName, type: "group" });
      setChannels(prev => [...prev, res.data]);
      setActiveChannel(res.data);
      setNewChannelName("");
      setShowNew(false);
      toast.success("Channel created");
    } catch { toast.error("Failed"); }
  };

  const escalateMessage = (msg) => { setEscSourceMsg(msg); setShowEscDialog(true); };

  const openQuickEscalation = (categoryId) => {
    setQuickEscCategory(categoryId);
    setEscSourceMsg(null);
    setShowEscDialog(true);
  };

  const handleEscCreated = () => { loadEscalations(); setShowEscDialog(false); };

  const handleEscUpdate = (updatedEsc) => {
    setEscalations(prev => prev.map(e => e.escalation_id === updatedEsc.escalation_id ? updatedEsc : e));
    setSelectedEsc(updatedEsc);
    loadEscalations();
  };

  const openEscDetail = (esc) => { setSelectedEsc(esc); setEscDrawerOpen(true); };

  const filteredEscalations = escalations.filter(e => {
    if (escFilter === "all") return true;
    if (escFilter === "new") return e.status === "new";
    if (escFilter === "urgent") return ["urgent", "critical"].includes(e.severity);
    if (escFilter === "mine") return e.owner_id === user?.user_id;
    if (escFilter === "open") return !["resolved", "closed"].includes(e.status);
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-screen"><RefreshCw className="w-6 h-6 animate-spin text-rose-400" /></div>;

  return (
    <div data-testid="team-hub-page" className="flex h-[calc(100vh-0px)] bg-[#FAFAFA]">
      <QuickEscalateDialog open={showEscDialog} onClose={() => { setShowEscDialog(false); setQuickEscCategory(null); }} onCreated={handleEscCreated} sourceMessage={escSourceMsg} locations={locations} />
      <EscalationDrawer esc={selectedEsc} open={escDrawerOpen} onClose={() => setEscDrawerOpen(false)} onUpdate={handleEscUpdate} staff={staff} locations={locations} />

      {/* Sidebar: Channels + Escalation Nav */}
      <div className="w-[260px] bg-white border-r border-slate-100 flex flex-col flex-shrink-0">
        {/* Tab Toggle */}
        <div className="flex border-b border-slate-100">
          <button onClick={() => setTab("chat")} data-testid="tab-chat"
            className={`flex-1 py-3 text-sm font-semibold text-center transition-colors ${tab === "chat" ? "text-rose-600 border-b-2 border-rose-500" : "text-slate-400 hover:text-slate-600"}`}>
            <MessageSquare className="w-4 h-4 inline mr-1.5" />Chat
          </button>
          <button onClick={() => setTab("escalations")} data-testid="tab-escalations"
            className={`flex-1 py-3 text-sm font-semibold text-center transition-colors relative ${tab === "escalations" ? "text-rose-600 border-b-2 border-rose-500" : "text-slate-400 hover:text-slate-600"}`}>
            <AlertTriangle className="w-4 h-4 inline mr-1.5" />Issues
            {escStats?.open > 0 && (
              <span className="absolute top-2 right-4 w-5 h-5 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center">{escStats.open}</span>
            )}
          </button>
        </div>

        {tab === "chat" && (
          <>
            <div className="p-3 border-b border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Channels</span>
                <button onClick={() => setShowNew(!showNew)} className="text-slate-400 hover:text-rose-500 transition-colors"><Plus className="w-4 h-4" /></button>
              </div>
              {showNew && (
                <div className="flex gap-1.5 mb-2">
                  <Input value={newChannelName} onChange={e => setNewChannelName(e.target.value)} placeholder="Channel name" className="h-8 text-xs border-slate-200 rounded-lg" data-testid="new-channel-input" />
                  <Button size="sm" onClick={createChannel} className="h-8 bg-slate-900 text-white rounded-lg px-3"><Check className="w-3 h-3" /></Button>
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto py-1">
              {channels.map(ch => (
                <button key={ch.channel_id} data-testid={`channel-${ch.channel_id}`}
                  onClick={() => { setActiveChannel(ch); loadMessages(ch.channel_id); }}
                  className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left transition-colors ${
                    activeChannel?.channel_id === ch.channel_id ? "bg-rose-50 text-rose-600" : "text-slate-600 hover:bg-slate-50"
                  }`}>
                  <Hash className="w-4 h-4 flex-shrink-0 opacity-50" />
                  <span className="text-sm font-medium truncate">{ch.name}</span>
                </button>
              ))}
            </div>
            {/* Quick Escalate Actions */}
            <div className="border-t border-slate-100 p-3">
              <button onClick={() => setShowQuickActions(!showQuickActions)}
                className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 hover:text-rose-500 transition-colors mb-2 w-full">
                <Zap className="w-3 h-3" /> Quick Actions <ChevronDown className={`w-3 h-3 ml-auto transition-transform ${showQuickActions ? "" : "-rotate-90"}`} />
              </button>
              {showQuickActions && (
                <div className="grid grid-cols-2 gap-1.5">
                  {QUICK_ACTIONS.map(qa => (
                    <button key={qa.id} onClick={() => openQuickEscalation(qa.id)} data-testid={`quick-action-${qa.id}`}
                      className="flex items-center gap-1.5 px-2 py-2 rounded-lg border border-slate-200 text-[10px] font-semibold text-slate-600 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 transition-all">
                      <qa.icon className="w-3 h-3" strokeWidth={1.5} />
                      {qa.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {tab === "escalations" && (
          <div className="flex-1 flex flex-col">
            {/* Escalation Filters */}
            <div className="p-3 border-b border-slate-100 space-y-2">
              <Button onClick={() => setShowEscDialog(true)} className="w-full h-9 gradient-primary text-white rounded-lg text-xs font-semibold" data-testid="new-escalation-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New Escalation
              </Button>
              <div className="flex flex-wrap gap-1">
                {[
                  { id: "all", label: "All" },
                  { id: "new", label: "New" },
                  { id: "urgent", label: "Urgent" },
                  { id: "mine", label: "My Issues" },
                  { id: "open", label: "Open" },
                ].map(f => (
                  <button key={f.id} onClick={() => setEscFilter(f.id)} data-testid={`esc-filter-${f.id}`}
                    className={`px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all ${escFilter === f.id ? "bg-rose-100 text-rose-600" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Stats Strip */}
            {escStats && (
              <div className="px-3 py-2 border-b border-slate-100 flex gap-3" data-testid="esc-stats-strip">
                <div className="text-center">
                  <p className="text-lg font-semibold text-slate-900 font-['Outfit']">{escStats.open}</p>
                  <p className="text-[9px] text-slate-400 uppercase">Open</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-rose-500 font-['Outfit']">{(escStats.by_severity?.urgent || 0) + (escStats.by_severity?.critical || 0)}</p>
                  <p className="text-[9px] text-slate-400 uppercase">Urgent</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-blue-500 font-['Outfit']">{escStats.by_status?.new || 0}</p>
                  <p className="text-[9px] text-slate-400 uppercase">New</p>
                </div>
              </div>
            )}
            {/* List */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {filteredEscalations.map(esc => (
                <EscalationCard key={esc.escalation_id} esc={esc} onClick={openEscDetail} locations={locations} />
              ))}
              {filteredEscalations.length === 0 && (
                <div className="text-center py-8 text-slate-400 text-sm font-['Manrope']">No escalations match filter</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {activeChannel && tab === "chat" ? (
          <>
            {/* Channel Header */}
            <div className="h-14 border-b border-slate-100 flex items-center px-5 bg-white">
              <Hash className="w-4 h-4 text-slate-400 mr-2" />
              <h2 className="font-['Outfit'] text-lg font-semibold text-slate-900">{activeChannel.name}</h2>
              <span className="text-xs text-slate-400 ml-3 font-['Manrope']">{messages.length} messages</span>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {messages.map(msg => (
                <div key={msg.message_id} data-testid={`message-${msg.message_id}`}
                  className={`group flex gap-3 ${msg.message_type === "system" || msg.message_type === "quick_action" ? "justify-center" : ""}`}>
                  {msg.message_type !== "system" && msg.message_type !== "quick_action" && (
                    <div className="w-9 h-9 rounded-xl gradient-primary flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                      {msg.sender_name?.[0] || "?"}
                    </div>
                  )}
                  <div className={`flex-1 min-w-0 ${msg.message_type === "system" || msg.message_type === "quick_action" ? "text-center" : ""}`}>
                    {msg.message_type === "system" || msg.message_type === "quick_action" ? (
                      <p className="text-xs text-slate-400 bg-slate-100 rounded-full px-4 py-1.5 inline-block font-['Manrope']">{msg.content}</p>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="text-sm font-semibold text-slate-900">{msg.sender_name}</span>
                          <span className="text-[10px] text-slate-400">{new Date(msg.created_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                          {/* Escalate Button */}
                          <button onClick={() => escalateMessage(msg)} data-testid={`escalate-msg-${msg.message_id}`}
                            className="opacity-0 group-hover:opacity-100 ml-auto flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold text-rose-500 bg-rose-50 hover:bg-rose-100 transition-all">
                            <ArrowUp className="w-3 h-3" /> Escalate
                          </button>
                        </div>
                        <p className="text-sm text-slate-700 font-['Manrope'] leading-relaxed">{msg.content}</p>
                        {msg.source_message && (
                          <div className="mt-1.5 bg-slate-50 rounded-lg px-3 py-2 border-l-2 border-slate-300">
                            <p className="text-xs text-slate-500 italic font-['Manrope']">{msg.source_message}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
              <div ref={msgsEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-slate-100 bg-white p-4">
              <div className="flex gap-2">
                <Input value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder={`Message #${activeChannel.name}...`}
                  className="h-11 border-slate-200 rounded-xl text-sm" data-testid="message-input"
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }}} />
                <Button onClick={sendMessage} disabled={sending} className="h-11 bg-slate-900 text-white rounded-xl px-5" data-testid="send-message-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : tab === "escalations" ? (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-8">
            <div className="w-16 h-16 rounded-2xl gradient-secondary flex items-center justify-center mb-4 shadow-lg shadow-violet-500/20">
              <AlertTriangle className="w-7 h-7 text-white" strokeWidth={1.5} />
            </div>
            <h3 className="font-['Outfit'] text-xl font-semibold text-slate-700 mb-1">Smart Escalation Routing</h3>
            <p className="text-sm text-slate-400 font-['Manrope'] text-center max-w-sm mb-6">
              Select an escalation from the sidebar to view details, update status, assign ownership, and add comments.
            </p>
            <div className="grid grid-cols-3 gap-4 max-w-md">
              {[
                { icon: Zap, label: "Auto-routed", desc: "Issues go to the right queue" },
                { icon: Clock, label: "SLA tracked", desc: "Time-sensitive resolution" },
                { icon: Shield, label: "Ownership", desc: "Every issue has an owner" },
              ].map(f => (
                <div key={f.label} className="text-center">
                  <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center mx-auto mb-2">
                    <f.icon className="w-4 h-4 text-violet-500" strokeWidth={1.5} />
                  </div>
                  <p className="text-xs font-semibold text-slate-700">{f.label}</p>
                  <p className="text-[10px] text-slate-400">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <p className="font-['Manrope']">Select a channel to start messaging</p>
          </div>
        )}
      </div>

      {/* Right Sidebar: Team Presence */}
      <div className="w-[200px] bg-white border-l border-slate-100 p-4 flex-shrink-0 hidden xl:block">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Team Online</p>
        <div className="space-y-2">
          {presence.filter(p => p.status !== "offline").map(p => (
            <div key={p.user_id} className="flex items-center gap-2">
              <div className="relative">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-xs font-semibold" style={{ backgroundColor: p.color || "#94A3B8" }}>
                  {p.name?.[0]}
                </div>
                <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${p.status === "online" ? "bg-emerald-500" : "bg-amber-500"}`} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-medium text-slate-700 truncate">{p.name}</p>
                <p className="text-[10px] text-slate-400 truncate">{p.status_text || p.status}</p>
              </div>
            </div>
          ))}
          {presence.filter(p => p.status !== "offline").length === 0 && (
            <p className="text-xs text-slate-400 font-['Manrope']">No one online</p>
          )}
        </div>
      </div>
    </div>
  );
}
